import { useQuery } from '@tanstack/react-query';

import { queryClient } from '../components/queryClient';
import { Fleet, Permit } from '../interfaces';
import { RoadworksService } from '../services/roadworks.service';
import { useHasPermission } from '../hooks';

const key = 'roadworks';

export const useRoadworkPermitQuery = (id: string, isEnabled: boolean) => {
  const queryKey = [key, id];
  const hasPermission = useHasPermission('roadworks:read:list');

  const queryFn = () => RoadworksService.getRoadworkPermit(id);

  return useQuery({
    queryKey,
    queryFn,
    enabled: !!id && hasPermission && isEnabled,
  });
};

export const useRoadworkPermitsQuery = (fleet: Fleet) => {
  const queryKey = [key, 'list', fleet.uuid];
  const isEnabled = useHasPermission('roadworks:read:list');

  const queryFn = () =>
    RoadworksService.getRoadworkPermits(
      { dateFrom: '1980-01-01T00:00:00.000Z', dateTo: '2099-12-31T23:59:59.999Z', limit: 99999 },
      fleet,
    );

  return useQuery({
    queryKey,
    queryFn,
    enabled: isEnabled,
  });
};

/**
 *
 * @param event Event to put into cache
 *
 * @description This function is used to put an event into the query cache,
 * and to invalidate the query, so that the next time the event is requested (via hook)
 * it will be fetched from the server BUT before the server responds, the cached data
 * will be displayed (instantenously) so that the app will feel snappier.
 */
export const setRoadworkQueryCache = (data: Permit) => {
  // Put data (or partial data) into cache
  queryClient.setQueryData([key, data.id], data);

  // Make sure we refetch the event in the background, WHEN NEEDED - but display
  // the cached data in the meantime, instantenously
  queryClient.invalidateQueries([key, data.id]);
};

export const invalidateRoadworkPermits = async () => {
  queryClient.invalidateQueries({ queryKey: [key], refetchType: 'all' });
};

export const setPermitPreview = (data: Permit | null) => {
  queryClient.setQueryData([key, 'preview'], data);
};

export const usePermitPreview = () => {
  return useQuery<Permit | null>({
    queryKey: [key, 'preview'],
    enabled: true,
    queryFn: () => null,
    staleTime: Infinity, // never stale
    cacheTime: Infinity, // never garbage collected
  });
};
